// Should this screen offer a Project Management launch link?
//
// Two answers from one question to the API (GET /pm-launch/:projectId/pages/:page/access):
//   enabled   — the server has not stopped the feature (it is on unless PM_LAUNCH_ENABLED
//               is set to false, the emergency off). Decides whether Access Management
//               lists the grant at all.
//   available — this user would be given a link for this project. Decides whether the
//               link itself is shown.
//
// The feature is normally ON, so this is asked afresh on each mount of the Attrition tab
// and of Access Management: a grant made a minute ago shows on the next visit. The link is
// shown only on the server's explicit "yes" — anything else (a 404 from an older API, a
// network error, a sign-in problem, a missing project) reads as "no", and the screen is
// exactly as it was before the feature existed.
//
// What is remembered: only a definite "stopped on this server" (a 2xx saying so, or a
// 404 = the API does not have the route), and only for five minutes. While the feature is
// stopped, the Dashboard then makes at most one small request every five minutes instead
// of one per tab visit, and a page left open picks up the stop being lifted the next time
// the tab is opened after that.
//
// Screens that ask at the same moment, for the same project and page, share ONE request:
// a hiring panel draws the Manage Budgets link on each opening's card, and five cards
// must not be five requests. Only the request in flight is shared, and only for a few
// seconds: once it has answered, or has been out that long without an answer, the next
// ask is a new request. So nothing above changes, and a request that never answers
// cannot keep the link off a page for as long as the page stays open.
import { useEffect, useState } from 'react';
import { pmLaunchAPI, type PmLaunchPageKey } from '../../services/api';

export interface PmLaunchStatus {
  enabled: boolean;
  available: boolean;
}

const OFF: PmLaunchStatus = { enabled: false, available: false };

/** How long a definite "switched off" is believed without asking again. */
export const PM_LAUNCH_OFF_MEMO_MS = 5 * 60 * 1000;
let switchedOffUntil = 0;
const rememberSwitchedOff = (): void => {
  switchedOffUntil = Date.now() + PM_LAUNCH_OFF_MEMO_MS;
};

/** How long a request that has not answered is shared with the screens that ask after it. */
export const PM_LAUNCH_SHARE_MS = 15 * 1000;
/** The requests that have not answered yet, by project and page. An entry leaves when its answer arrives. */
const inFlight = new Map<string, { asked: Promise<PmLaunchStatus>; at: number }>();

export const loadPmLaunchStatus = (projectId: number, page: PmLaunchPageKey): Promise<PmLaunchStatus> => {
  if (Date.now() < switchedOffUntil) return Promise.resolve(OFF);
  const key = `${projectId}:${page}`;
  const already = inFlight.get(key);
  if (already && Date.now() - already.at < PM_LAUNCH_SHARE_MS) return already.asked;
  try {
    // Its own entry only: a later request for the same project and page may have taken the place.
    const answered = (): void => {
      if (inFlight.get(key)?.asked === asked) inFlight.delete(key);
    };
    const asked: Promise<PmLaunchStatus> = pmLaunchAPI.getAccess({ projectId, page }).then(
      (res) => {
        answered();
        const data = res?.data?.data;
        const enabled = data?.enabled === true;
        // Only the server's own "false" is a definite off; a malformed answer is not.
        if (data?.enabled === false) rememberSwitchedOff();
        return { enabled, available: enabled && data?.available === true };
      },
      (e: unknown) => {
        answered();
        if ((e as { response?: { status?: number } })?.response?.status === 404) rememberSwitchedOff();
        return OFF;
      },
    );
    inFlight.set(key, { asked, at: Date.now() });
    return asked;
  } catch {
    // The API module could not even build the request: treat as off, ask again later.
    return Promise.resolve(OFF);
  }
};

/** Tests only: forget that the server said the feature is switched off, and any request still out. */
export const resetPmLaunchStatusForTests = (): void => {
  switchedOffUntil = 0;
  inFlight.clear();
};

/** `active` = false skips the request (a tab that is not showing, a viewer who cannot use the answer). */
export const usePmLaunchStatus = (projectId: number | null | undefined, page: PmLaunchPageKey, active = true): PmLaunchStatus => {
  const [status, setStatus] = useState<PmLaunchStatus>(OFF);
  useEffect(() => {
    // A new project starts from "no" — the previous project's answer is not this one's.
    setStatus(OFF);
    if (!active || !projectId) return undefined;
    let alive = true;
    void loadPmLaunchStatus(projectId, page).then((next) => { if (alive) setStatus(next); });
    return () => { alive = false; };
  }, [projectId, page, active]);
  return status;
};

/** Where the web's own "Opening…" page for a launch lives. Opened in a new tab. */
export const pmLaunchHref = (projectId: number, page: PmLaunchPageKey): string =>
  `/open/project-management/${page}?project=${projectId}`;
